import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { UploadSimple, Trash, FileCsv } from "@phosphor-icons/react";
import api, { API } from "@/lib/api";

export default function SalesTab() {
  const [batches, setBatches] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [creators, setCreators] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [label, setLabel] = useState(monthLabel());
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const fileRef = useRef();

  function monthLabel() {
    const d = new Date();
    return d.toLocaleString("en-US", { month: "long", year: "numeric" });
  }

  async function loadAll() {
    const [b, p, c] = await Promise.all([
      api.get("/sales/batches"),
      api.get("/purchases"),
      api.get("/creators"),
    ]);
    setBatches(b.data); setPurchases(p.data); setCreators(c.data);
  }
  useEffect(() => { loadAll(); }, []);

  async function upload(e) {
    e.preventDefault();
    const f = fileRef.current?.files?.[0];
    if (!f) return toast.error("Pick a CSV file");
    if (!label.trim()) return toast.error("Enter a batch label (e.g. October 2026)");
    setUploading(true);
    setLastResult(null);
    try {
      const fd = new FormData();
      fd.append("label", label.trim());
      fd.append("file", f);
      const token = localStorage.getItem("gtarp_token");
      const r = await fetch(`${API}/sales/upload`, {
        method: "POST", body: fd,
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Upload failed");
      setLastResult(data);
      toast.success(`Imported ${data.total_rows} rows · ${data.new_customers} new customers`);
      fileRef.current.value = "";
      loadAll();
    } catch (err) {
      toast.error(String(err.message || err));
    } finally { setUploading(false); }
  }

  async function del(id, name) {
    if (!window.confirm(`Delete batch "${name}" and all its rows?`)) return;
    try {
      const r = await api.delete(`/sales/batches/${id}`);
      toast.success(`Removed ${r.data.removed_purchases} rows`);
      if (selectedBatch === id) setSelectedBatch(null);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  const nameByCreator = Object.fromEntries(creators.map(c => [c.id, c.display_name]));
  const shown = selectedBatch ? purchases.filter(p => p.batch_id === selectedBatch) : purchases;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="display-heading font-black text-3xl">Sales · Tebex CSV Import</h2>
        <div className="kbd-label text-white/40 mt-1">UPLOAD TEBEX PAYMENT EXPORT · BUILD CUSTOMER PROFILES · ATTRIBUTE VIA LINKED DISCORD MEMBERS</div>
      </div>

      <form onSubmit={upload} className="card-tactical p-6 space-y-4">
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <div className="kbd-label mb-1">BATCH LABEL</div>
            <input value={label} onChange={(e) => setLabel(e.target.value)} data-testid="sales-batch-label" placeholder="e.g. October 2026" className="w-full bg-black/60 border border-white/10 px-3 py-2 font-mono text-sm focus:border-neon-cyan outline-none" />
          </div>
          <div className="md:col-span-2">
            <div className="kbd-label mb-1">CSV FILE</div>
            <input ref={fileRef} type="file" accept=".csv" data-testid="sales-csv-file" className="w-full bg-black/60 border border-white/10 px-3 py-2 font-mono text-xs file:mr-3 file:bg-neon-cyan file:text-black file:border-0 file:px-3 file:py-1 file:kbd-label" />
          </div>
        </div>
        <div className="flex items-end justify-between gap-4">
          <p className="text-xs text-white/50 font-mono max-w-2xl">
            Drop your <span className="text-neon-cyan">Tebex payments export</span> (Transaction ID, Date, Status, Username, Uuid, Price, Tax, Currency, Name, Email, Package Names…). <span className="text-white/70">Declined/failed rows are skipped.</span> Each email becomes a customer profile — link them to Discord members in the <span className="text-neon-yellow">CUSTOMERS</span> tab to attribute revenue.
          </p>
          <button type="submit" disabled={uploading} data-testid="sales-upload-submit" className="neon-btn px-6 py-3 inline-flex items-center gap-2 disabled:opacity-50">
            <UploadSimple size={16} weight="bold" /> {uploading ? "UPLOADING…" : "UPLOAD BATCH"}
          </button>
        </div>
      </form>

      {lastResult && (
        <div className="card-tactical p-5 border-l-4 border-l-neon-cyan">
          <div className="flex items-center justify-between mb-2">
            <div className="display-heading font-bold">LAST UPLOAD · {lastResult.label}</div>
            <div className="kbd-label text-white/60">{lastResult.filename}</div>
          </div>
          <div className="flex gap-8 font-mono text-sm flex-wrap">
            <div><span className="kbd-label text-white/40">IMPORTED</span><div className="text-2xl stat-value">{lastResult.total_rows}</div></div>
            <div><span className="kbd-label text-emerald-400">ATTRIBUTED</span><div className="text-2xl stat-value text-emerald-400">{lastResult.attributed_rows}</div></div>
            <div><span className="kbd-label text-neon-yellow">UNLINKED</span><div className="text-2xl stat-value text-neon-yellow">{lastResult.unattributed_rows}</div></div>
            <div><span className="kbd-label text-white/40">SKIPPED (DECLINED)</span><div className="text-2xl stat-value text-white/60">{lastResult.declined_skipped}</div></div>
            <div><span className="kbd-label text-neon-cyan">NEW CUSTOMERS</span><div className="text-2xl stat-value text-neon-cyan">{lastResult.new_customers}</div></div>
          </div>
          {lastResult.unattributed_rows > 0 && (
            <p className="text-xs text-white/60 font-mono mt-3">→ {lastResult.unattributed_rows} rows aren't linked to a creator yet. Go to <span className="text-neon-yellow">CUSTOMERS</span> to link buyers to Discord members.</p>
          )}
        </div>
      )}

      <div className="card-tactical">
        <div className="px-6 py-4 border-b border-white/10 flex justify-between items-center">
          <h3 className="display-heading font-bold text-lg">BATCHES</h3>
          <button onClick={() => setSelectedBatch(null)} className="kbd-label text-white/50 hover:text-neon-cyan">SHOW ALL</button>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="kbd-label text-left text-white/50 border-b border-white/10">
            <th className="px-6 py-3">LABEL</th><th>UPLOADED</th><th>FILE</th><th>ATTRIBUTED</th><th>UNLINKED</th><th>SKIPPED</th><th>TOTALS</th><th></th>
          </tr></thead>
          <tbody className="font-mono">
            {batches.map(b => (
              <tr key={b.id} className={`border-b border-white/5 cursor-pointer hover:bg-white/[0.03] ${selectedBatch === b.id ? "bg-white/[0.04]" : ""}`} onClick={() => setSelectedBatch(b.id)} data-testid={`batch-row-${b.id}`}>
                <td className="px-6 py-3 font-sans">{b.label}</td>
                <td className="text-xs text-white/50">{b.uploaded_at?.slice(0, 10)}</td>
                <td className="text-xs text-white/60"><FileCsv size={12} className="inline" /> {b.filename}</td>
                <td className="text-emerald-400">{b.attributed_rows ?? b.matched_rows ?? 0}</td>
                <td className="text-neon-yellow">{b.unattributed_rows ?? b.unmatched_rows ?? 0}</td>
                <td className="text-white/50">{b.declined_skipped ?? "—"}</td>
                <td className="text-neon-yellow">{Object.entries(b.currency_totals || {}).map(([k, v]) => `${k} ${v}`).join(", ") || "—"}</td>
                <td className="px-3">
                  <button onClick={(e) => { e.stopPropagation(); del(b.id, b.label); }} data-testid={`batch-delete-${b.id}`} className="ghost-btn px-2 py-1 text-xs text-white inline-flex items-center gap-1 hover:!border-red-500">
                    <Trash size={12} /> DELETE
                  </button>
                </td>
              </tr>
            ))}
            {batches.length === 0 && <tr><td colSpan={8} className="px-6 py-6 text-white/40">No batches yet. Upload your first Tebex CSV above.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="card-tactical">
        <div className="px-6 py-4 border-b border-white/10 flex justify-between items-center">
          <h3 className="display-heading font-bold text-lg">
            {selectedBatch ? `PURCHASES · ${batches.find(b => b.id === selectedBatch)?.label || "Batch"}` : "ALL PURCHASES"}
          </h3>
          <span className="kbd-label text-white/40">{shown.length} ROWS</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="kbd-label text-left text-white/50 border-b border-white/10">
              <th className="px-6 py-3">CUSTOMER</th><th>CREATOR</th><th>KIND</th><th>AMOUNT</th><th>PACKAGE</th><th>BATCH</th>
            </tr></thead>
            <tbody className="font-mono">
              {shown.slice(0, 500).map(p => (
                <tr key={p.transaction_id} className="border-b border-white/5">
                  <td className="px-6 py-3">
                    <div className="font-sans">{p.customer_name || p.tebex_username || "—"}</div>
                    <div className="text-[10px] text-white/40">{p.customer_email || p.customer_uuid || ""}</div>
                  </td>
                  <td className="font-sans">{p.creator_id ? (nameByCreator[p.creator_id] || p.creator_id) : <span className="text-white/30">unlinked</span>}</td>
                  <td className={p.kind === "payment" ? "text-emerald-400" : "text-red-400"}>{p.kind}</td>
                  <td>${p.amount} {p.currency}</td>
                  <td className="text-white/60 text-xs truncate max-w-[180px]">{p.package_names || "—"}</td>
                  <td className="text-neon-yellow text-xs">{p.batch_label || "—"}</td>
                </tr>
              ))}
              {shown.length === 0 && <tr><td colSpan={6} className="px-6 py-6 text-white/40">No purchases.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
